import { X, Calendar, User } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useCandidateWithDetails } from '@/hooks/useCandidateWithDetails'
import { CandidateRoleBadge } from './CandidateRoleBadge'
import { LoyaltyStatus } from './LoyaltyStatus'
import { SkillsDisplay } from './SkillsDisplay'
import { TraitsDisplay } from './TraitsDisplay'
import { IdeologyComparison } from '@/components/ideology/IdeologyComparison'
import { PoliticalLabel } from '@/components/ideology/PoliticalLabel'
import { RECRUITMENT_SOURCE_INFO } from '@/constants/recruitmentSources'
import type { IdeologyVector } from '@/constants/ideology'
import { cn } from '@/lib/utils'

interface CandidateDetailModalProps {
  candidateId: string | null
  isOpen: boolean
  onClose: () => void
  partyIdeology: IdeologyVector
  isOwner: boolean
  onChangeRole?: (candidateId: string) => void
}

function RecognitionDisplay({
  recognition,
  className,
}: {
  recognition: number
  className?: string
}) {
  const percentage = Math.round(recognition)

  function getRecognitionLabel(value: number): string {
    if (value >= 80) return 'Famous'
    if (value >= 60) return 'Well-known'
    if (value >= 40) return 'Recognized'
    if (value >= 20) return 'Emerging'
    return 'Unknown'
  }

  function getRecognitionColor(value: number): string {
    if (value >= 60) return 'bg-blue-500'
    if (value >= 40) return 'bg-cyan-500'
    return 'bg-gray-400'
  }

  function getRecognitionDescription(value: number): string {
    if (value >= 80) return 'Household name, high media profile'
    if (value >= 60) return 'Recognized by most voters'
    if (value >= 40) return 'Known in political circles'
    if (value >= 20) return 'Building a public profile'
    return 'Mostly unknown outside party circles'
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Recognition</span>
        <span className="text-sm font-semibold">{percentage}%</span>
      </div>

      <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full transition-all', getRecognitionColor(recognition))}
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold uppercase text-muted-foreground">
          {getRecognitionLabel(recognition)}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">
        {getRecognitionDescription(recognition)}
      </p>
    </div>
  )
}

function getRoleDescription(
  role: string,
  districtName: string | undefined
): string {
  switch (role) {
    case 'party_leader':
      return 'The face of the party and primary spokesperson.'
    case 'regional_candidate':
      return districtName
        ? `Assigned to run in ${districtName} for elections.`
        : 'Assigned to run in a district for elections.'
    case 'list_candidate':
      return 'On the party list for proportional representation seats.'
    case 'minister':
      return 'Serving in a government ministry position.'
    case 'chair':
      return 'Chairs a parliamentary committee.'
    default:
      return 'Not currently assigned to any role.'
  }
}

export function CandidateDetailModal({
  candidateId,
  isOpen,
  onClose,
  partyIdeology,
  isOwner,
  onChangeRole,
}: CandidateDetailModalProps) {
  const { candidate, isLoading, error } = useCandidateWithDetails(
    isOpen ? candidateId ?? undefined : undefined
  )

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Candidate Details</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        {isLoading && (
          <div className="flex h-40 items-center justify-center">
            <div className="text-muted-foreground">Loading candidate...</div>
          </div>
        )}

        {error && (
          <div className="flex h-40 items-center justify-center">
            <div className="text-destructive">Failed to load candidate</div>
          </div>
        )}

        {candidate && (
          <div className="space-y-6">
            {/* Header */}
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{candidate.name}</h2>
                  <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>Age {candidate.age}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <CandidateRoleBadge
                  role={candidate.role}
                  districtName={candidate.district?.name}
                />
                <Badge variant="secondary">
                  {RECRUITMENT_SOURCE_INFO[candidate.recruitment_source].name}
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <Calendar className="h-3 w-3" />
                  Recruited tick {candidate.recruited_tick}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Status Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Status
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <LoyaltyStatus loyalty={candidate.loyalty} />
                </div>
                <div className="rounded-lg border p-4">
                  <RecognitionDisplay recognition={candidate.recognition} />
                </div>
              </div>
            </div>

            <Separator />

            {/* Skills Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Skills
              </h3>
              <div className="rounded-lg border p-4">
                <SkillsDisplay
                  charisma={candidate.charisma}
                  admin_skill={candidate.admin_skill}
                  debate_skill={candidate.debate_skill}
                />
              </div>
            </div>

            <Separator />

            {/* Traits Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Traits
              </h3>
              <div className="rounded-lg border p-4">
                <TraitsDisplay
                  scandal_prone={candidate.scandal_prone}
                  ambition={candidate.ambition}
                  traits={candidate.traits}
                />
              </div>
            </div>

            <Separator />

            {/* Ideology Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Ideology
              </h3>
              {/* Political Position Label */}
              <PoliticalLabel
                ideology={candidate.ideology_vector}
                showSubLabel={true}
                size="md"
                variant="card"
              />
              {/* Detailed comparison with party */}
              <div className="rounded-lg border p-4">
                <IdeologyComparison
                  vectorA={candidate.ideology_vector}
                  vectorB={partyIdeology}
                  labelA="Candidate"
                  labelB="Party"
                />
              </div>
            </div>

            <Separator />

            {/* Current Role Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Current Role
              </h3>
              <div className="rounded-lg border p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <CandidateRoleBadge
                        role={candidate.role}
                        districtName={candidate.district?.name}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {getRoleDescription(candidate.role, candidate.district?.name)}
                    </p>
                  </div>
                  {isOwner && onChangeRole && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onChangeRole(candidate.id)}
                    >
                      Change Role
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
